/** カード共通: 媒体ごとの状態（Guard 結果・個別承認・投稿結果・結果不明の解決） */

import { useState } from 'react'
import { ExternalLink } from 'lucide-react'
import type { PostChannelRow } from '../../lib/postTypes'
import { platformLabel } from '../../lib/platform'
import { isGuardPending, needsGatedApproval } from '../../lib/queueRules'
import type { QueueActions } from './useQueueActions'

/** 媒体の状態 → 状態チップ（gm-chip＋文字。apply-plan-2026-10-09 §5.5 の対応表） */
const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  guard_ok: { label: '安全チェック済み', cls: 'gm-chip-ok' },
  gated_out: { label: '要確認', cls: 'gm-chip-warn' },
  blocked: { label: '投稿不可', cls: 'gm-chip-bad' },
  pending: { label: 'チェック中', cls: 'gm-chip-neutral' },
  publishing: { label: '投稿中', cls: 'gm-chip-info' },
  published: { label: '投稿済み', cls: 'gm-chip-ok' },
  failed: { label: '失敗', cls: 'gm-chip-bad' },
  skipped: { label: 'スキップ', cls: 'gm-chip-neutral' },
}

function statusBadge(ch: PostChannelRow) {
  // 前回の投稿試行の結果が不明（下の解消 UI で人が確定させる）
  if (ch.unresolved) return { label: '結果不明', cls: 'gm-chip-warn' }
  // GATED_OUT を個別承認済みなら、要確認のままに見せない（audit C11）
  if (ch.status === 'gated_out' && ch.humanApprovedBy) {
    return { label: '確認済み', cls: 'gm-chip-info' }
  }
  return STATUS_LABEL[ch.status] ?? STATUS_LABEL.pending
}

function channelName(ch: PostChannelRow): string | null {
  if (!ch.channel) return null
  return `${platformLabel(ch.channel.platform)} ${ch.channel.handle}`
}

export function ChannelRows({ channels, actions, readOnly = false }: {
  channels: PostChannelRow[]
  actions: QueueActions
  /** true なら個別承認ボタンを出さない（投稿中など）。結果不明の解決は常に出す */
  readOnly?: boolean
}) {
  if (channels.length === 0) return null
  return (
    <div className="mt-2 space-y-1">
      {channels.map(ch => {
        const g = statusBadge(ch)
        const name = channelName(ch)
        // 表示中の Guard 結果が古い版のもの（編集直後など）。'pending' はバッジ自体が「チェック中」
        const guardPending = ch.guardCurrent === false && ['guard_ok', 'gated_out', 'blocked'].includes(ch.status)
        const showViolations = ch.status === 'gated_out' || ch.status === 'blocked'
        return (
          <div key={ch.id} className="space-y-1">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {name && <span className="text-ink-muted">{name}</span>}
              <span className={`gm-chip ${g.cls}`}>{g.label}</span>
              {/* 表示中の結果は前の版のもの。今の版をチェックし直している */}
              {guardPending && (
                <span className="gm-chip gm-chip-neutral">再チェック中</span>
              )}
              {ch.externalUrl && (
                <a href={ch.externalUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-bold text-primary underline">
                  投稿を見る <ExternalLink size={12} aria-hidden />
                </a>
              )}
              {!readOnly && needsGatedApproval(ch) && (
                <button
                  disabled={actions.pending || isGuardPending(ch)}
                  onClick={() => actions.approveGated.mutate(ch.id)}
                  className="gm-btn gm-btn-secondary gm-btn-sm">
                  理由を確認して承認
                </button>
              )}
            </div>
            {showViolations && !!ch.guardResult?.violations?.length && (
              <ul className="ml-1 list-disc space-y-0.5 pl-4 text-xs text-ink-muted">
                {ch.guardResult.violations.map((v, i) => (
                  <li key={i}>{v.reason}{v.quote ? `「${v.quote}」` : ''}</li>
                ))}
              </ul>
            )}
            {/* 投稿は成立したが確かめてほしいこと（エラーではないので、エラーとしては出さない） */}
            {ch.note
              ? <NotePanel ch={ch} actions={actions} />
              : ch.error && <p className="text-xs text-bad">エラー: {ch.error}</p>}
            {ch.unresolved && <UnresolvedPanel ch={ch} actions={actions} />}
          </div>
        )
      })}
    </div>
  )
}

/** 投稿は成立したが確かめてほしいこと（例: Instagram がキャプションを付けずに公開した）。取り消せないので、直してから確認済みにする */
function NotePanel({ ch, actions }: { ch: PostChannelRow; actions: QueueActions }) {
  return (
    <div role="alert" className="rounded-ctl bg-warn-bg p-3 text-xs text-ink">
      <p className="font-bold text-warn">投稿済み（要確認）</p>
      <p className="mt-0.5">{ch.note}</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {ch.externalUrl && (
          <a href={ch.externalUrl} target="_blank" rel="noreferrer" className="gm-btn gm-btn-secondary">
            投稿を開く <ExternalLink size={12} aria-hidden />
          </a>
        )}
        <button disabled={actions.pending} onClick={() => actions.ackNote.mutate(ch.id)} className="gm-btn gm-btn-secondary">
          確認しました
        </button>
      </div>
    </div>
  )
}

/**
 * 前回の投稿試行の結果が不明な媒体。X/WP 側で既に公開済みかもしれないので、
 * 二重投稿を避けるため人に確認してもらってから「済み」か「再投稿可」に倒す
 */
function UnresolvedPanel({ ch, actions }: { ch: PostChannelRow; actions: QueueActions }) {
  const [url, setUrl] = useState('')
  const inputId = `resolve-url-${ch.id}`
  return (
    <div role="alert" className="rounded-ctl bg-warn-bg p-3 text-xs text-ink">
      <p className="font-bold text-warn">前回の投稿結果が不明です。{ch.channel ? platformLabel(ch.channel.platform) : '投稿先'} 側を確認してください</p>
      <p className="mt-0.5">既に投稿されている可能性があります。確認せずに再投稿すると二重投稿になることがあります。</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <label htmlFor={inputId} className="sr-only">投稿の URL（任意）</label>
        <input id={inputId} value={url} onChange={e => setUrl(e.target.value)} type="url"
          placeholder="投稿の URL（任意）"
          className="gm-input min-w-52 flex-1" />
        <button disabled={actions.pending}
          onClick={() => actions.resolve.mutate({
            pchId: ch.id,
            body: { action: 'mark_published', ...(url.trim() ? { externalUrl: url.trim() } : {}) },
          })}
          className="gm-btn gm-btn-secondary gm-btn-sm">
          投稿されていた
        </button>
        <button disabled={actions.pending}
          onClick={() => actions.resolve.mutate({ pchId: ch.id, body: { action: 'retry' } })}
          className="gm-btn gm-btn-secondary gm-btn-sm">
          投稿されていなかった
        </button>
      </div>
      {/* ボタンは短く、押した結果はここで説明する（長いラベルは狭い画面で収まらない — Codex デザインレビュー） */}
      <p className="mt-1.5 text-xs">「投稿されていた」は投稿済みとして記録し、「投稿されていなかった」は再投稿できる状態に戻します。</p>
    </div>
  )
}
